import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { sharedReportService } from '../services/sharedReportService';
import { normalizeReport } from '../utils/reportTransform';
import type { RawReportPayload } from '../types/report';
import { useToast } from '../hooks/useToast';
import { useTranslate } from '../hooks/useTranslate';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useDataAdmission } from '../contexts/DataAdmissionContext';
import ErrorBoundary from '../components/ErrorBoundary';
import { CinematicThemeProvider } from '../contexts/CinematicThemeContext';
import { ReportThemeProvider, useReportTheme } from '../contexts/ReportThemeContext';
import { CinematicExecutiveSummary } from '../components/report/CinematicExecutiveSummary';
import { CinematicHealthAndDimensions } from '../components/report/CinematicHealthAndDimensions';
import { CinematicProblemSolutions } from '../components/report/CinematicProblemSolutions';
import type { SharedReportDetail } from '../types/shared';
import type { Report } from '../types';
import { downloadReportAsPDF } from '../utils/reportDownload';
import { IoIosCloudDownload } from 'react-icons/io';

export function SharedReportPage() {
    return (
        <ReportThemeProvider defaultTheme="gradient-steel">
            <SharedReportPageContent />
        </ReportThemeProvider>
    );
}

function SharedReportPageContent() {
    const { getThemeClasses } = useReportTheme();
    const themeClasses = getThemeClasses();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { push: pushToast } = useToast();
    const { t } = useTranslate();
    const { activeLanguage } = useLanguage();
    const { isAuthenticated, user } = useAuth();
    const { silentRefresh } = useDataAdmission();

    const reportId = searchParams.get('reportId');
    // Safe access: use user?.email from context instead of authManager.getUserEmail()
    // which throws if auth hasn't fully initialized. The useEffect below
    // guards with !userEmail so no API call fires until email is available.
    const userEmail = user?.email || '';

    const [sharedDetail, setSharedDetail] = useState<SharedReportDetail | null>(null);
    const [report, setReport] = useState<Report | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const handleDownloadPDF = async () => {
        setIsExporting(true);
        pushToast({ variant: 'info', message: t('Generating PDF — please wait...'), ttl: 6000 });
        try {
            const success = await downloadReportAsPDF(report?.dataset?.name || 'shared-report');
            if (success) {
                pushToast({ variant: 'success', message: t('PDF downloaded successfully'), ttl: 5000 });
            } else {
                pushToast({ variant: 'error', message: t('Failed to generate PDF. Please try again.'), ttl: 6000 });
            }
        } catch {
            pushToast({ variant: 'error', message: t('Failed to generate PDF'), ttl: 5000 });
        } finally {
            setIsExporting(false);
        }
    };

    useEffect(() => {
        if (!reportId) {
            setError('No report ID provided');
            setLoading(false);
            return;
        }

        // Wait for auth to be fully initialized before making API calls
        if (!isAuthenticated || !userEmail) {
            return;
        }

        let cancelled = false;

        async function fetchSharedReport() {
            setLoading(true);
            setError(null);
            try {
                const detail = await sharedReportService.getSharedReport(reportId!);
                if (cancelled) return;
                setSharedDetail(detail);

                // Transform raw_report into the format normalizeReport expects
                // (same transformation as useReport for historical reports)
                const backendReport = detail.raw_report as Record<string, unknown>;

                // Transform detailed_problems
                const transformedProblems = ((backendReport.detailed_problems as any[]) || []).map((problem: any) => ({
                    Issue: problem.issue || '',
                    Problem_Details: problem.problem_details || problem.description || '',
                    Solution_Strategy: problem.solution_strategy || '',
                    Business_Impact: problem.business_impact || '',
                    Technical_Analysis: problem.technical_analysis || {},
                    Action_Plan: problem.action_plan || [],
                    column_name: problem.column_name || problem.Column_Name
                }));

                // Transform health_score
                const transformedHealthScore = { ...(backendReport.health_score as any) };
                if (transformedHealthScore.data_quality_assessment_6_dimensions) {
                    transformedHealthScore['Data Quality Assessment (6 Dimensions)'] = transformedHealthScore.data_quality_assessment_6_dimensions;
                    delete transformedHealthScore.data_quality_assessment_6_dimensions;
                }

                // Transform executive_summary (ensure array)
                let executiveSummary = (backendReport.executive_summary as any) || [];
                if (!Array.isArray(executiveSummary)) {
                    executiveSummary = [executiveSummary];
                }

                // Extract data_object_description
                const dataObjectDescription =
                    (backendReport.data_object_description as any) ||
                    (backendReport.health_score as any)?.data_object_description ||
                    null;
                const hasValidDataObjectDescription = dataObjectDescription &&
                    typeof dataObjectDescription === 'object' &&
                    (dataObjectDescription.total_rows !== undefined ||
                        dataObjectDescription.total_columns !== undefined ||
                        dataObjectDescription.description ||
                        dataObjectDescription.dataset_name ||
                        dataObjectDescription.table_name);

                // Extract data object name
                const dataObjectName =
                    (backendReport.dataset_name as string) ||
                    (backendReport.data_object_name as string) ||
                    (backendReport.original_filename as string) ||
                    dataObjectDescription?.dataset_name ||
                    dataObjectDescription?.table_name ||
                    dataObjectDescription?.name ||
                    undefined;

                const reportData = {
                    'Health Score': transformedHealthScore,
                    'Executive Summary': executiveSummary,
                    'Detailed Problems': transformedProblems,
                    'Analysis Metadata': (backendReport.analysis_metadata as any) || {},
                    'reasoning_result': backendReport.reasoning_result,
                    'dq_framework_results': backendReport.dq_framework_results,
                    ...(hasValidDataObjectDescription && { 'Data Object Description': dataObjectDescription })
                };

                const normalized = normalizeReport(reportData as RawReportPayload, dataObjectName);
                setReport(normalized);
                // Determine save state:
                // - Owner viewing own report: can_save=false, but don't show "Saved"
                // - Recipient who already saved: can_save=false, show "Saved"
                // - Recipient who hasn't saved: can_save=true, show "Add to My Reports"
                const isOwner = !!userEmail && detail.shared_by_email.toLowerCase() === userEmail.toLowerCase();
                setSaved(!detail.can_save && !isOwner);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : 'Failed to load shared report');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchSharedReport();
        return () => { cancelled = true; };
    }, [reportId, activeLanguage, isAuthenticated, userEmail]);

    const handleSave = useCallback(async () => {
        if (!reportId) return;
        setIsSaving(true);
        try {
            await sharedReportService.saveSharedReport(reportId);
            setSaved(true);
            pushToast({ variant: 'success', message: t('Report saved to your Shared Reports'), ttl: 4000 });
            // Refresh context so home screen shows the new shared report immediately
            silentRefresh().catch(() => {});
        } catch {
            pushToast({ variant: 'error', message: t('Failed to save report'), ttl: 4000 });
        } finally {
            setIsSaving(false);
        }
    }, [reportId, pushToast, t]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 flex items-center justify-center">
                <div className="simple-spinner-large" />
            </div>
        );
    }

    if (error || !report || !sharedDetail) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 flex items-center justify-center">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
                    <div className="text-5xl mb-4">🔗</div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">{t('Report Not Found')}</h2>
                    <p className="text-gray-600 mb-6">{error || t('This shared report link may be invalid or expired.')}</p>
                    <button
                        onClick={() => navigate('/datasources')}
                        className="px-6 py-2 bg-red-600 text-white rounded-full font-semibold hover:bg-red-700 transition-colors"
                    >
                        {t('Go to Data Sources')}
                    </button>
                </div>
            </div>
        );
    }

    const isOwner = !!userEmail && sharedDetail.shared_by_email.toLowerCase() === userEmail.toLowerCase();

    return (
        <ErrorBoundary>
            <CinematicThemeProvider defaultVariant="vibrant">
                <div className={`min-h-screen ${themeClasses.pageBackground}`}>
                    {/* Sticky top bar with all controls */}
                    <div
                        className="sticky top-0 z-50 report-navigation-buttons flex items-center justify-between px-6 py-3"
                        style={{
                            background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                            boxShadow: '0 4px 14px -2px rgba(220, 38, 38, 0.4)'
                        }}
                    >
                        {/* Left: Back button */}
                        <button
                            onClick={() => navigate('/datasources')}
                            className="inline-flex items-center justify-center px-4 py-2 text-white font-semibold rounded-full transition-all duration-200 whitespace-nowrap hover:bg-white/15"
                            aria-label={t('Close')}
                        >
                            <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            {t('Back')}
                        </button>

                        {/* Center: Shared by info */}
                        <div className="flex items-center gap-2 text-white text-sm font-semibold">
                            <span>{t('Shared by')} {sharedDetail.shared_by_email.split('@')[0]}</span>
                            <span className="text-white/60">·</span>
                            <span className="text-white/80 text-xs">{new Date(sharedDetail.generated_at).toLocaleDateString()}</span>
                        </div>

                        {/* Right: Download + Action button */}
                        <div className="flex items-center gap-3">
                            {/* Download PDF */}
                            <button
                                type="button"
                                onClick={handleDownloadPDF}
                                disabled={isExporting}
                                className="inline-flex items-center justify-center w-10 h-10 text-white rounded-full transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed hover:bg-white/15"
                                aria-label={t('Download as PDF')}
                                title={t('Download as PDF')}
                            >
                                {isExporting ? (
                                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                ) : (
                                    <IoIosCloudDownload className="w-5 h-5" />
                                )}
                            </button>

                            {/* Action button */}
                            {isOwner ? (
                                <div className="inline-flex items-center px-4 py-2 text-white rounded-full text-sm font-semibold bg-white/15">
                                    {t('Your Report')}
                                </div>
                            ) : saved ? (
                                <div className="inline-flex items-center gap-1.5 px-4 py-2 text-white rounded-full text-sm font-semibold bg-white/15">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    {t('Saved')}
                                </div>
                            ) : (
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 text-white rounded-full text-sm font-bold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed bg-white/15 hover:bg-white/25"
                                >
                                    {isSaving ? (
                                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                    )}
                                    {t('Add to My Reports')}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Report Content */}
                    <div>
                        <CinematicExecutiveSummary
                            summary={report.summary}
                            dataObjectDescription={report.dataObjectDescription}
                            executiveSummary={report.executiveSummary}
                            reasoningResult={report.reasoningResult}
                            datasetName={report.dataset.name}
                        />
                        <CinematicHealthAndDimensions
                            healthScore={report.healthScore.overall}
                            healthDimensions={report.healthScore.dimensions}
                            datasetMeta={{
                                name: report.dataset.name,
                                rows: report.dataset.rowCount,
                                columns: report.dataset.columnCount
                            }}
                            userRulesData={null}
                        />
                        <CinematicProblemSolutions problems={report.problems} />
                    </div>
                </div>
            </CinematicThemeProvider>
        </ErrorBoundary>
    );
}
